import { useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { Plus, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

/**
 * The helpdesk tags a rule applies alongside its action.
 *
 * Written as well as picked. A plain multi-select could only offer tags that already existed, and
 * the executor forwards whatever is here straight to the helpdesk as free text — so a brand whose
 * first rule needs a tag nobody has used yet had no way to name it.
 *
 * Comparison is case-insensitive everywhere: helpdesks fold "Refund" and "refund" into one tag, so
 * keeping both here would promise a distinction that does not survive the write.
 */

interface TagComboboxProps {
	value: string[];
	onChange: (tags: string[]) => void;
	/** Tags already in use elsewhere on the account. Suggestions, never a whitelist. */
	suggestions?: string[];
	placeholder?: string;
	/**
	 * Read-only. Used for Kim-authored rules, where the save call sends channel scope alone, so an
	 * editable tag field would accept a change and then discard it.
	 */
	disabled?: boolean;
}

/** The form a tag is compared in. Storage keeps the casing the brand typed. */
const foldTag = (tag: string): string => tag.trim().toLowerCase();

const containsTag = (tags: string[], tag: string): boolean =>
	tags.some((existing) => foldTag(existing) === foldTag(tag));

/** First-seen casing wins, so a suggestion cannot silently re-case a tag already chosen. */
const uniqueTags = (tags: string[]): string[] => {
	const seen = new Set<string>();
	return tags.filter((tag) => {
		const folded = foldTag(tag);
		if (folded === '' || seen.has(folded)) return false;
		seen.add(folded);
		return true;
	});
};

export const TagCombobox = ({
	value,
	onChange,
	suggestions = [],
	placeholder = 'Type or select a tag',
	disabled = false
}: TagComboboxProps) => {
	const [open, setOpen] = useState(false);
	const [draft, setDraft] = useState('');
	// null means "nothing arrowed to", which is what makes Enter add the typed words rather than
	// whichever suggestion happens to sit at the top of the list.
	const [activeIndex, setActiveIndex] = useState<number | null>(null);
	const fieldRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	// Chosen tags stay in the list. A rule loaded for editing carries tags that may exist nowhere
	// else, and removing one would otherwise make it unpickable again without retyping it.
	const options = useMemo(() => uniqueTags([...value, ...suggestions]), [value, suggestions]);

	const query = draft.trim();
	const visibleOptions = useMemo(
		() => options.filter((option) => option.toLowerCase().includes(query.toLowerCase())),
		[options, query]
	);

	const canCreate = query !== '' && !containsTag(options, query);
	const items = canCreate ? [...visibleOptions, query] : visibleOptions;

	const addTag = (tag: string) => {
		const trimmed = tag.trim();
		// Nothing typed and a tag already on the rule are both no-ops. Clearing the box regardless is
		// the honest feedback: the state the brand asked for — that tag, once — already holds.
		if (trimmed !== '' && !containsTag(value, trimmed)) onChange([...value, trimmed]);
		setDraft('');
		setActiveIndex(null);
	};

	const removeTag = (tag: string) => onChange(value.filter((existing) => foldTag(existing) !== foldTag(tag)));

	const toggleOption = (tag: string) => (containsTag(value, tag) ? removeTag(tag) : addTag(tag));

	const moveActive = (step: number) => {
		if (items.length === 0) return;
		setOpen(true);
		setActiveIndex((current) => {
			if (current === null) return step > 0 ? 0 : items.length - 1;
			return (current + step + items.length) % items.length;
		});
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter') {
			event.preventDefault();
			const active = activeIndex === null ? undefined : items[activeIndex];
			if (active === undefined) addTag(draft);
			else toggleOption(active);
			return;
		}

		if (event.key === 'Backspace' && draft === '' && value.length > 0) {
			removeTag(value[value.length - 1]!);
			return;
		}

		if (event.key === 'Escape' && open) {
			// This field lives inside a Dialog, which closes on its own Escape. Losing the half-filled
			// form because a menu was open is not what dismissing a menu means.
			event.stopPropagation();
			setOpen(false);
			setActiveIndex(null);
			return;
		}

		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			event.preventDefault();
			moveActive(event.key === 'ArrowDown' ? 1 : -1);
		}
	};

	const handleRemoveClick = (event: MouseEvent<HTMLButtonElement>, tag: string) => {
		// The field focuses its input on click; without this, removing a chip would also reopen the menu.
		event.stopPropagation();
		removeTag(tag);
	};

	return (
		<Popover open={open && !disabled} onOpenChange={(next) => !disabled && setOpen(next)}>
			<PopoverAnchor asChild>
				<div
					ref={fieldRef}
					/* h-10 / rounded-xl / gray-500, the same field as every other one in the modal. This
					   was the outlier the design review named: a 36px box with a 6px radius sitting
					   directly under two 40px boxes with 12px radii. */
					className={cn(
						'flex min-h-10 w-full flex-wrap items-center gap-1 rounded-[12px] border border-gray-500 bg-white px-2 py-1 transition-[color,box-shadow]',
						disabled
							? 'cursor-not-allowed'
							: 'focus-within:border-gray-600 focus-within:ring-[1.95px] focus-within:ring-gray-500'
					)}
					onClick={() => !disabled && inputRef.current?.focus()}
				>
					{value.map((tag) => (
						<Badge key={tag} variant='secondary' className='gap-1 px-2 py-1 text-[11px] font-normal'>
							{tag}
							{!disabled && (
								<button
									type='button'
									aria-label={`Remove ${tag}`}
									className='text-muted-foreground hover:text-foreground'
									onClick={(event) => handleRemoveClick(event, tag)}
								>
									<X className='size-3' />
								</button>
							)}
						</Badge>
					))}

					<input
						ref={inputRef}
						role='combobox'
						aria-expanded={open}
						aria-autocomplete='list'
						aria-label='Add tags'
						value={draft}
						disabled={disabled}
						placeholder={value.length === 0 ? placeholder : ''}
						className='h-7 min-w-24 flex-1 bg-transparent text-xs md:text-xs outline-none placeholder:text-xs placeholder:text-gray-800'
						onChange={(event) => {
							setDraft(event.target.value);
							setActiveIndex(null);
							setOpen(true);
						}}
						onFocus={() => setOpen(true)}
						onKeyDown={handleKeyDown}
					/>
				</div>
			</PopoverAnchor>

			<PopoverContent
				align='start'
				className='w-[var(--radix-popover-trigger-width)] rounded-2xl p-1.5'
				// Focus belongs in the input the whole time — the menu is something typing filters,
				// not somewhere the brand is sent.
				onOpenAutoFocus={(event) => event.preventDefault()}
				onInteractOutside={(event) => {
					if (fieldRef.current?.contains(event.target as Node)) event.preventDefault();
				}}
			>
				<div role='listbox' aria-multiselectable aria-label='Tags'>
					{items.length === 0 ? (
						<p className='px-2 py-2 text-[13px] text-muted-foreground'>Type a tag and press Enter to add it</p>
					) : (
						items.map((item, index) => {
							const isCreate = canCreate && index === items.length - 1;
							const selected = !isCreate && containsTag(value, item);

							return (
								<div
									key={isCreate ? `create:${item}` : item}
									role='option'
									aria-selected={selected}
									data-active={index === activeIndex}
									className={cn(
										'flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 text-[13px] text-foreground hover:bg-muted',
										index === activeIndex && 'bg-muted'
									)}
									onMouseEnter={() => setActiveIndex(index)}
									onClick={() => (isCreate ? addTag(item) : toggleOption(item))}
								>
									{/*
									 * A real checkbox, not a tick that appears from nowhere. An unselected row
									 * drew an INVISIBLE check, so the list showed a floating mark beside the
									 * chosen tags and blank space beside the rest — nothing said the rows were
									 * toggles at all, or that an unticked one could be ticked. The empty box
									 * is the affordance; the tick alone was only ever the result of it.
									 *
									 * `pointer-events-none` because the row owns the click. Two hit targets
									 * for one action means the box can toggle and then the row toggles it
									 * back.
									 */}
									{isCreate ? (
										<Plus className='size-3.5 shrink-0' />
									) : (
										<Checkbox checked={selected} className='pointer-events-none shrink-0' tabIndex={-1} />
									)}
									<span className='truncate'>{isCreate ? `Add “${item}”` : item}</span>
								</div>
							);
						})
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
};
